import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { BookingDetailTab, GetDeliveryOrderDetailRequestDto, XpressDeliveryOrderDetail } from '@shared/models';
import { SharedDeliveryOrderService } from '@shared/services/shared-delivery-order.service';
import {
	ServiceTypeEnum,
    XpressDeliveryStatus
} from 'app/models';
import { SignalrService } from 'app/services';
import { Utils } from 'app/utils';
import { OrderUtils } from 'app/utils/order-utils';
import { MessageService } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';

@Component({
	selector: 'booking-details-sidebar',
	templateUrl: './booking-details-sidebar.component.html',
	styleUrl: './booking-details-sidebar.component.scss'
})
export class BookingDetailsSidebarComponent implements OnInit, OnChanges, OnDestroy {
	@Input() isOpen: boolean = false;
	@Input() orderId: number = 0;
	@Input() headerIcon?: string = 'local_shipping';
	@Input() visibleTabs: BookingDetailTab[] = [
		BookingDetailTab.Details,
		BookingDetailTab.CustomerProfile,
		BookingDetailTab.People,
		BookingDetailTab.Payment,
		BookingDetailTab.Messages,
		BookingDetailTab.Timeline,
		BookingDetailTab.Assigning
	];

	private destroy$ = new Subject<void>();
	@Output() closed = new EventEmitter<void>();
	@Output() cancelTrip = new EventEmitter<number>();
	@Output() completeTrip = new EventEmitter<number>();
	@Output() forceAssign = new EventEmitter<number>();
	@Output() prioritizeRequest = new EventEmitter<number>();

	selectedTabIndex: number = 0;
	selectedTabLabel: string = '';
	loading: boolean = false;
	isRefreshing: boolean = false;
	loadFailed: boolean = false;
	deliveryOrderDetails!: XpressDeliveryOrderDetail;

	// Expose enum to template
	BookingDetailTab = BookingDetailTab;

	$deliveryOrders: Observable<any>;
	$deliveryOrdersSubject: Subscription | undefined;

	constructor(
		private deliveryOrderService: SharedDeliveryOrderService,
		private messageService: MessageService,
		private signalRService: SignalrService,
		private cdr: ChangeDetectorRef
	) {
		this.$deliveryOrders = this.signalRService.DeliveryOrdersObservable;
	}

	ngOnInit(): void {
		if (this.orderId > 0) {
			this.getRiderOrderDetail();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		const orderIdChanged = changes['orderId'] && changes['orderId'].currentValue > 0;
		const openChanged = changes['isOpen'];

		if (orderIdChanged) {
			this.getRiderOrderDetail();
			this.subscribeToSignalR();
		}

		if (openChanged && openChanged.currentValue === true && this.orderId > 0) {
			this.subscribeToSignalR();
		}

		if (openChanged && openChanged.currentValue === false) {
			this.unsubscribeFromSignalR();
		}
	}

	private subscribeToSignalR(): void {
		this.unsubscribeFromSignalR();

		if (this.isOpen && this.orderId > 0) {
			this.$deliveryOrdersSubject = this.$deliveryOrders.subscribe((data: any) => {
				if (!this.loading && !this.isRefreshing && this.isOpen && this.orderId > 0) {
					this.getRiderOrderDetail(true);
				}
			});
		}
	}

	private unsubscribeFromSignalR(): void {
		if (this.$deliveryOrdersSubject) {
			this.$deliveryOrdersSubject.unsubscribe();
			this.$deliveryOrdersSubject = undefined;
		}
	}

	async getRiderOrderDetail(isBackgroundRefresh: boolean = false) {
		if (isBackgroundRefresh) {
			this.isRefreshing = true;
		} else {
			this.loading = true;
		}

		this.loadFailed = false;
		this.cdr.markForCheck();

		try {
			const dto: GetDeliveryOrderDetailRequestDto = {
				XpressDeliveryOrderId: this.orderId,
				TimeZoneOffset: Utils.getTimezoneOffset()
			};

			this.deliveryOrderDetails = await this.deliveryOrderService.getDeliveryOrderDetail(dto, this.messageService);

			if (!this.deliveryOrderDetails?.Success) {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: this.deliveryOrderDetails?.ErrorMessage });
				this.loading = false;
				this.isRefreshing = false;
				this.loadFailed = true;
				this.cdr.markForCheck();
				return;
			}
			this.loading = false;
			this.isRefreshing = false;
			this.loadFailed = false;
			this.cdr.markForCheck();
		} catch (err) {
			this.loading = false;
			this.isRefreshing = false;
			this.loadFailed = true;
			this.cdr.markForCheck();
			throw err;
		}
	}

	onClose() {
		this.unsubscribeFromSignalR();
		this.orderId = 0;
		this.closed.emit();
	}

	onCancelTrip() {
		this.cancelTrip.emit(this.orderId);
	}

	onCompleteTrip() {
		this.completeTrip.emit(this.orderId);
	}

	onForceAssign() {
		this.forceAssign.emit(this.orderId);
	}

	onPrioritizeRequest() {
		if (this.deliveryOrderDetails.IsPriority) {
			this.messageService.add({ severity: 'info', summary: 'Duplicate Request', detail: 'Request already prioritized.' });
			return;
		}
		this.prioritizeRequest.emit(this.orderId);
	}

	onTabChange(event: { index: number; tab: any }) {
		this.selectedTabIndex = event.index;
		this.selectedTabLabel = event.tab?.label || '';
	}

	isTabVisible(tab: BookingDetailTab): boolean {
		return this.visibleTabs.includes(tab);
	}

	retryLoad() {
		this.getRiderOrderDetail();
	}

	ngOnDestroy() {
		this.unsubscribeFromSignalR();
		this.destroy$.next();
		this.destroy$.complete();

		this.orderId = 0;
	}

	isCancelButtonVisible(): boolean {
		if (!this.deliveryOrderDetails) {
			return false;
		}

		if (this.deliveryOrderDetails.ServiceTypeId === ServiceTypeEnum.Connex
			&& this.deliveryOrderDetails.StatusId === XpressDeliveryStatus.New) {
			return true;
		}

		return this.isCompleteButtonVisible();
	}

	isCompleteButtonVisible(): boolean {
		if (!this.deliveryOrderDetails) {
			return false;
		}

		const statuses: XpressDeliveryStatus[] = [
			XpressDeliveryStatus.Completed,
			XpressDeliveryStatus.New,
			XpressDeliveryStatus.Canceled,
			XpressDeliveryStatus.Rejected,
			XpressDeliveryStatus.Expired,
			XpressDeliveryStatus.RepeatScheduled,
			XpressDeliveryStatus.RiderAssigning];

		if (!statuses.includes(this.deliveryOrderDetails.StatusId)) {
			return true;
		}

		return false;
	}

	isPrioritizeRequestVisible(): boolean {
		if (!this.deliveryOrderDetails) {
			return false;
		}

		const statuses: XpressDeliveryStatus[] = [
			XpressDeliveryStatus.New, XpressDeliveryStatus.RiderAssigning];

		if (statuses.includes(this.deliveryOrderDetails.StatusId)) {
			return true;
		}

		return false;
	}

	isForceAssignVisible(): boolean {
		if (!this.isPrioritizeRequestVisible()) {
			return false;
		}

		return OrderUtils.isForceAssignVisible(
			this.deliveryOrderDetails.ServiceTypeId,
			this.deliveryOrderDetails.IsScanAndRide,
			this.deliveryOrderDetails.ClientLocationType
		);
	}
}
